import type { ReactNode } from 'react';
import {
  Pressable,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { tokens } from '../../theme/tokens';

type AppIconButtonVariant = 'solid' | 'soft' | 'ghost';
type AppIconButtonSize = 'compact' | 'regular';

type AppIconButtonProps = {
  accessibilityLabel: string;
  icon: ReactNode;
  onPress?: () => void;
  variant?: AppIconButtonVariant;
  size?: AppIconButtonSize;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
};

export function AppIconButton({
  accessibilityLabel,
  icon,
  onPress,
  variant = 'soft',
  size = 'regular',
  disabled = false,
  style,
}: AppIconButtonProps) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [
        styles.base,
        styles[size],
        styles[variant],
        pressed && !disabled ? styles.pressed : null,
        disabled ? styles.disabled : null,
        style,
      ]}
    >
      {icon}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    borderRadius: tokens.component.iconButton.radius,
    alignItems: 'center',
    justifyContent: 'center',
  },
  regular: {
    width: tokens.component.iconButton.size,
    height: tokens.component.iconButton.size,
  },
  compact: {
    width: tokens.component.iconButton.compactSize,
    height: tokens.component.iconButton.compactSize,
  },
  solid: {
    backgroundColor: tokens.colors.primary,
  },
  soft: {
    backgroundColor: tokens.colors.surfaceMuted,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
  },
  ghost: {
    backgroundColor: 'transparent',
  },
  pressed: {
    opacity: 0.84,
  },
  disabled: {
    opacity: 0.5,
  },
});
